/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React, { Component } from 'react';
import {
    object, string, shape, bool, arrayOf, func,
} from 'prop-types';
import { connect } from 'react-redux';
import LazyLoad from 'react-lazyload';
import ReactHTMLParser from 'react-html-parser';
import Media from 'react-media';
import { withStyles } from '@material-ui/core/styles';
import { Fade } from '@material-ui/core';
import { bindActionCreators } from 'redux';
import { CirclePlay } from 'lucide-react';
import { actions as tagManagerActions } from '../../../../../../../state/ducks/TagManager';
import { emitCustomTrackingEvent } from '../../../../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';
import isProductListingAd from '../../../../../../helpers/isProductListingAd';
import SmartGift from '../../SmartGift/SmartGift';
import { shouldRenderSnipe, snipePath, isSnipeImageActive } from '../../common/commonProductPageHelper';
import GraphqlClubPDPImage from '../../GraphqlClubPDPImage/GraphqlClubPDPImage';
import { clubComponentType } from '../../common/types';
import SnipeRender from '../../common/SnipeRender';
import SnipeRenderQuery from '../../common/SnipeRenderQuery';
import ResponsiveImage from '../../../../GraphqlCommonComponents/ResponsiveImage/ResponsiveImage';
import { getPmallSummaryInfo } from '../../../../../../../state/ducks/Common/Common-Selectors';
import { pmallPluginConfig } from '../../../../../../../state/ducks/Common/ducks/PmallPersonalization/PmallPersonalization-Helper';
import SmartGiftContainer from '../../SmartGiftContainer';
import CarouselAltImg from '../CarouselAltImg';
import VideoComponent from '../VideoComponent';
import ProductImageZoomingUI from './ProductImageZoomingUI';
import FavoritesUI from '../../../../GraphqlCommonComponents/Favourite/FavouriteUi';

const { sdkURL } = pmallPluginConfig;

const styles = (theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        background: theme?.palette?.white,
        [theme?.breakpoints?.down('md')]: {
            flexDirection: 'column',
            justifyContent: 'flex-start',
        },
    },
    altImageContainer: {
        gridArea: 'productImageAlt',
        display: 'inline-block',
        flexWrap: 'wrap',
        marginRight: '10px',
        maxWidth: '110px',
        [theme?.breakpoints?.down('md')]: {
            order: '2',
            margin: '20px 0 0 0',
            display: 'flex',
            maxWidth: '100%',
        },
    },
    circlePlay: {
        position: 'absolute',
        '& svg': {
            width: '4em',
            height: '4em',
            mixBlendMode: 'overlay',
        },
    },
    altImageButton: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 0,
        position: 'relative',
        width: '100%',
        height: 'auto',
        border: 'none',
        outline: 'none', // TODO: Need to add outline for accessibility
        cursor: 'pointer',
        '& img': {
            display: 'block',
        },
        [theme?.breakpoints?.down('md')]: {
            maxWidth: '110px',
            width: '100%',
        },
    },
    whiteBg: {
        background: '#FFF',
    },
    altImage: {
        width: '100%',
        height: 'auto',
    },
    altImageMobile: {
        margin: '0 auto',
    },
    productImageWrapper: {
        flex: '1 1 auto',
        order: '2',
        maxWidth: '600px',
        width: '100%',
        position: 'relative',
        [theme?.breakpoints?.down('md')]: {
            order: '1',
        },
    },
    productImageWrapperGrid: {
        flex: '1 1 auto',
        order: '2',
        maxWidth: '600px',
        width: '100%',
        [theme?.breakpoints?.down('md')]: {
            order: '1',
        },
    },
    productImage: {
        width: '100%',
        height: 'auto',
        display: 'block',
        background: 'linear-gradient(270deg, #E7EDF1, #F2F8FC, #E7EDF1)',
        position: 'relative',
        aspectRatio: '0.89/0.97',
        [theme?.breakpoints?.down('md')]: {
            height: 'auto',
        },
        '&>div': {
            width: '100%',
            height: '100%',
            '&>img': {
                height: '100%',
            },
        },
        '@media(hover: none)': {
            '& div[style*="opacity: 1"]:before': {
                display: 'block',
                position: 'absolute',
                content: '""',
                background: 'url("https://images.contentstack.io/v3/assets/blt95e0138311135214/bltd4026f47a9492630/630fcc337cb46356cd3a8cf2/close.png")',
                backgroundSize: 'cover',
                height: '30px',
                width: '30px',
                zIndex: '100',
                top: '5px',
                right: '5px',
            },
        },
        [theme?.breakpoints?.down('600')]: {
            height: '100%',
            '& >div': {
                height: '100%',
                '& img': {
                    height: 'auto',
                },
            },
        },
    },

    snipe: {
        position: 'absolute',
        top: '0px',
        right: '0',
        left: '0',
        width: '100%',
    },
    productImageWrapperClub: {
        display: 'inline-block',
        margin: '0',
        minWidth: '200px',
        maxWidth: '600px',
        height: '100%',
        position: 'relative',
        [theme?.breakpoints?.down('md')]: {
            marginLeft: '20px',
        },
        [theme?.breakpoints?.down('sm')]: {
            marginLeft: 0,
            maxWidth: '100%',
        },
    },
    responsiveWrapper: {
        position: 'relative',
    },
    monthSnipe: {
        position: 'absolute',
        right: 0,
        left: 0,
        bottom: 0,
        padding: 5,
        backgroundColor: 'rgba(47,47,47, .9)',
        boxSizing: 'border-box',
        color: theme.palette.cms?.primary_font_color || '#ffffff',
        textAlign: 'center',
    },
    upsellSelected: {
        '& span': {
            backgroundColor: theme.palette.primaryButton || '#a1001a',
        },
    },
    upsellImageBox: {
        display: 'inline-block',
        flexWrap: 'wrap',
        marginRight: '10px',
        maxWidth: '110px',
        [theme.breakpoints.down('md')]: {
            order: '2',
            margin: '20px 0 0 0',
            display: 'flex',
            maxWidth: '100%',
        },
        [theme.breakpoints.down('sm')]: {
            order: '2',
            margin: '20px 0 0 0',
            display: 'inline-block',
            maxWidth: '100%',
        },
        '&:hover span': {
            backgroundColor: theme.palette.primaryButton || '#a1001a',
        },
        '&:last-child': {
            margin: 0,
        },
    },
    flexCenter: {
        display: 'flex',
        justifyContent: 'center',
        '& .slick-list': {
            display: 'none',
        },
        [theme.breakpoints.down('md')]: {
            width: '92%',
            margin: '0 auto',
        },
        [theme.breakpoints.down('sm')]: {
            width: '95%',
            display: 'block',
            margin: '0 auto',
            '& .slick-slide': {
                float: 'left',
                whiteSpace: 'nowrap',
            },
            '& .slick-list': {
                display: 'block',
            },
        },
    },
    imageContainer: {
        position: 'relative',
        cursor: 'pointer',
    },
    videoAltButton: {
        '& img': {
            aspectRatio: '1/1.1',
        },
    },
    productImageWrapperInner: {
        gridArea: 'productImageGrid',
        position: 'relative',
    },
    simpleVideo: {
        width: '100%',
        minHeight: '597px',
        objectFit: 'cover',
        position: 'relative',
        [theme.breakpoints.down('md')]: {
            minHeight: 'auto',
        },
    },
    carouselContainer: {
        position: 'relative',
        width: '100%',
        '& .slick-list': {
            overflow: 'hidden',
            margin: '0px',
        },
        '& $altImageButton': {
            maxWidth: 'unset',
            width: '100px',
            padding: '0 10px 0px 0px',
            backgroundColor: theme.palette.cms?.white || '#ffffff',
            marginBottom: '0px',
        },
        '& $altImageContainer': {
            [theme?.breakpoints?.down('md')]: {
                margin: '0px',
            },
        },
        '& $altImage': {
            width: '100%',
        },
        '& .slick-slide': {
            display: 'inline-block',
        },
        '& .slick-prev, & .slick-next': {
            transform: 'translate(0,-50%)',
            cursor: 'pointer',
            border: 'none',
            outline: 0,
            fontSize: 0,
            lineHeight: 0,
            top: '48%',
            width: 30,
            height: 100,
            position: 'absolute',
            background: 'rgb(245 245 245 / 81%)',
            zIndex: 9999,
            '& svg': {
                height: 'inherit',
            },
        },
        '& .slick-prev': {
            left: 0,
        },
        '& .slick-next': {
            right: 0,
        },
    },
    mainSliderContainer: {
        display: 'grid',
        gridArea: 'productImageAlt',
        '&>div': {
            overflow: 'hidden',
        },
    },
    zoomCopyDesktop: {
        display: 'none',
        '@media (hover: hover)': {
            [theme.breakpoints.up(1280)]: {
                display: 'block',
                fontSize: '14px',
                lineHeight: '18px',
                margin: '10px 0px 0px',
            },
        },
    },
    upsellWrapper: {
        display: 'inline-block',
        flexWrap: 'wrap',
        '&:hover span': {
            backgroundColor: theme.palette.primaryButton || '#a1001a',
        },
    },
    magnifierContainer: {
        position: 'relative',
    },
});
class ProductImageUIGrid extends Component {
    constructor(props) {
        super(props);
        const {
            data: {
                name,
                image,
            },
            selectedSku,
            pmallSummaryInfo,
            selectedSkuBasedAltImages,
        } = this.props;

        const imageName = selectedSku?.image?.name || '';
        const imagePath = selectedSku?.image?.path || '';

        let defaultImagePath = this.buildImgPath(imagePath, imageName);
        const altImagesBaseLevelData = image?.additionalImages && [...image.additionalImages];
        const altImagesProdSkusData = selectedSku?.image?.additionalImages && [...selectedSku.image?.additionalImages];
        const productBaseAltImages = altImagesBaseLevelData?.length > 0 ? altImagesBaseLevelData : altImagesProdSkusData || [];
        const altImagesData = selectedSkuBasedAltImages ?  [...productBaseAltImages, ...altImagesProdSkusData] : productBaseAltImages;
        /**
         * This component is removed from the page on window resize which causes us to lose state.
         * When it returns, it should detect if there is a pmall preview avaialble. If
         * it is, initialize the component with it included in the altImages array
         * and set the default image to it.
         */

        if (pmallSummaryInfo?.customizations?.refnum) {
            defaultImagePath = this.buildImgPath(null, pmallSummaryInfo?.customizations?.imageURL);
            if (!altImagesData.includes(pmallSummaryInfo.customizations.imageURL)) {
                altImagesData.push(pmallSummaryInfo?.customizations?.imageURL);
            }
        }

        this.state = {
            path: imagePath,
            mainImage: {
                path: imageName,
                fullPath: defaultImagePath,
                imgName: name,
            },
            heroImage: defaultImagePath,
            altImages: altImagesData,
            modalOpen: false,
            selectedComponentMonth: 0,
            videoUrl: '',
            isVideo: false,
        };

        this.cdnLink = imagePath || 'https://cdn1.harryanddavid.com/wcsstore/HarryAndDavid/images/catalog/';
        this.defaultNoImage = 'https://images.contentstack.io/v3/assets/blt95e0138311135214/bltffae3c14fd3096e8/62e0138319ee366ebba54f3f/MissingImage.png'; // use for if image is missing
    }

    componentDidUpdate(prevProps) {
        const {
            selectedSku,
            pmallSummaryInfo,
            data: {
                image,
            },
            selectedSkuBasedAltImages,
        } = this.props;
        const { altImages } = this.state;
        let isAdditionalImages =  true;
        /**
         * This block runs when the user finishes the pmall iFrame workflow.
         * We need to inject pmall data into the alt images that came from the product data.
         *
         * A problem in the edit pmall workflow is that the url never changes. Because of that
         * the hero image, if already selected, never gets updated. We need to run this
         * block every time pmall finishes. Since it runs every time, we dont want to pile up
         * previews. Be sure to only ever add 1.
         */
        if (prevProps.pmallSummaryInfo !== pmallSummaryInfo) {
            const newAltImages = [...altImages];
            isAdditionalImages = false;
            const additionalImages =  selectedSkuBasedAltImages ? [...image?.additionalImages, ...selectedSku?.image?.additionalImages] : image?.additionalImages;
            if (!pmallSummaryInfo?.customizations?.imageURL) {
                const filterDefaultImgs = (additionalImages || []).filter((altImg) => altImg && !altImg.includes(sdkURL));
                this.setState({
                    altImages: filterDefaultImgs,
                }, this.setProductImage(selectedSku.image?.name));
            } else {
                if (!newAltImages.includes(pmallSummaryInfo?.customizations?.imageURL)) {
                    newAltImages.push(pmallSummaryInfo?.customizations?.imageURL);
                }
                this.setState({
                    altImages: newAltImages,
                },
                // once pmall is part of the alt images, make it the hero as well
                this.setProductImage(pmallSummaryInfo?.customizations?.imageURL));

                return;
            }
        }

        if (prevProps.selectedSku !== selectedSku && selectedSku.image) {
            this.setProductImage(selectedSku.image?.name);
            if (isAdditionalImages && selectedSku.image?.additionalImages?.length) {
                this.setState({
                    altImages: selectedSku.image?.additionalImages,
                });
            }
        }
    }

    buildImgPath = (path, name, isAlt) => {
        const { pmallSummaryInfo } = this.props;
        const alt = isAlt || false;
        if (!path || !name) {
            return this.defaultNoImage;
        }

        // eslint-disable-next-line no-nested-ternary
        // const extension = name.includes('_gif') ? '.gif' : alt ? 'c.jpg' : 'x.jpg';
        const extension = () => {
            let ext = 'x.jpg';
            if ((name.includes('_gif') && alt)) {
                ext = '.gif?format=jpg';
                return ext;
            }
            if ((name.includes('_gif') && !alt)) {
                ext = '.gif';
                return ext;
            }
            if (alt) {
                ext = 'c.jpg';
                return ext;
            }
            return ext;
        };
        if (name === pmallSummaryInfo?.customizations?.imageURL) {
            let url = `https:${pmallSummaryInfo?.customizations?.imageURL}`;
            /**
             * To prevent caching, add in a count parameter to the query. The imageURL
             * string recieved from the iFrame already contains a query.
             */
            url += `&cacheBust=${pmallSummaryInfo?.countCacheBust || 0}`;
            return url;
        }

        return `${path + name + extension()}`;
    };

    // If selected SKU image is 404 render parent image
    onImageError=(e) => {
        const { data, presentation_family } = this.props;
        if (data?.image?.path && presentation_family === 'food') {
            const { image } = data;
            e.target.src = this.buildImgPath(image.path, image.name);
        }
        return null;
    }

    getSlides = (items, isSlider = false) => {
        const slides = [];
        const { classes } = this.props;
        const { selectedComponentMonth } = this.state;
        (items || []).forEach((imgName, index) => {
            slides.push(
                (
                    <div
                        key={`btnImg-${imgName.month}`}
                        className={`${isSlider ? classes.upsellWrapper : classes.upsellImageBox} ${(items[selectedComponentMonth]?.month === imgName.month) ? classes.upsellSelected : ''}`}
                    >
                        <button
                            data-testid={`component-${index}`}
                            onClick={() => { this.setState({ modalOpen: true, selectedComponentMonth: index }); }}
                            type="button"
                            className={`${classes.altImageButton} altImageButton`}
                        >
                            <ResponsiveImage
                                path={`${this.cdnLink}${imgName.image?.name}x.jpg?width=110&height=124&auto=webp`}
                                alt={ReactHTMLParser(imgName.name)}
                                className={classes.altImage}
                            />
                            <span className={classes.monthSnipe}>{imgName.month?.substr(0, 3)}</span>
                        </button>
                    </div>
                ),
            );
        });
        return slides;
    }

    handleState = (obj) => {
        this.setState(obj);
    }

    /**
     * @description onClick handler for altImages to re-render hero image.
     * @note also used on componentDidUpdate.
     * @param {string} file
     * @param {string} contentType
     */
    setProductImage = (file, contentType = '') => {
        const { selectedSku, pmallSummaryInfo } = this.props;
        const { path } = this.state;
        let imageName = selectedSku?.image?.name || '';
        let imagePath = selectedSku?.image?.path || '';
        const defaultImagePath = imagePath && imageName ? this.buildImgPath(imagePath, imageName, false) : '';

        // intercept these variable and fine tune for pmall
        if (pmallSummaryInfo?.customizations?.imageURL) {
            imageName = pmallSummaryInfo?.customizations?.imageURL;
            imagePath = this.buildImgPath(null, imageName);
        }

        if (contentType) {
            this.setState({
                heroImage: this.buildImgPath(path, file),
                mainImage: {
                    path: imageName,
                    fullPath: defaultImagePath,
                    imgName: selectedSku.name,
                },
                isVideo: true,
                videoUrl: file,
            });
        } else {
            this.setState({
                heroImage: this.buildImgPath(path, file, false),
                mainImage: {
                    path: imageName,
                    fullPath: defaultImagePath,
                    imgName: selectedSku.name,
                },
                isVideo: false,
                videoUrl: '',
            });
        }
    };

    trackProductImageClick = () => {
        this.props.emitCustomTrackingEvent({
            eventType: 'custom',
            eventSubType: 'feature-tracking',
            action: 'Click',
            category: 'Product Image',
            label: 'Click on Image',
        });
    };

    renderZoom = () => {
        const { location, featureFlags } = this.props;
        const isPdpImageZoomEnabled = featureFlags['is-pdp-product-zoom-enabled'] || featureFlags['is-pdp-product-image-zoom-enabled'];
        const isPlaImageZoomEnabled = featureFlags['is-pla-product-zoom-enabled'];
        const isPla = isProductListingAd(location.search);
        if (isPla && isPlaImageZoomEnabled) return true;
        if (!isPla && isPdpImageZoomEnabled) return true;
        return false;
    }

    navigate = (type = 'next') => {
        const { selectedComponentMonth } = this.state;
        const { clubComponents } = this.props;
        const slidesLength = clubComponents?.length;
        if (type === 'next') {
            if ((slidesLength - 1) === selectedComponentMonth) {
                this.setState({ selectedComponentMonth: 0 });
            } else {
                this.setState({ selectedComponentMonth: (selectedComponentMonth + 1) });
            }
        } else if (type === 'prev') {
            if (selectedComponentMonth === 0) {
                this.setState({ selectedComponentMonth: (slidesLength - 1) });
            } else {
                this.setState({ selectedComponentMonth: (selectedComponentMonth - 1) });
            }
        }
    }

    getClubLengthDescription = () => {
        const { selectedSku } = this.props;
        const clubLength = selectedSku?.clubLength || 0;
        if (selectedSku?.date && selectedSku?.endDate) {
            return (
                <>
                    {`${clubLength} Months - ${selectedSku.date.month} ${selectedSku.date.year} - ${selectedSku.endDate.month} ${selectedSku.endDate.year}`}
                </>
            );
        }
        return null;
    }

    get3dSvg = () => (
        <svg className="jss25 jss785 jss779" focusable="false" viewBox="0 0 250 250" aria-hidden="true" role="presentation">
            <path fill="#B5B6B6" d="M124.62,246.52c-47.76,0-90.42-30.28-106.14-75.36l13.48-4.7c13.72,39.35,50.96,65.78,92.66,65.78c54.09,0,98.1-44.01,98.1-98.1H237C237,196.11,186.59,246.52,124.62,246.52" />
            <polygon fill="#B5B6B6" points="13,183.28 44.7,168.9 15.92,147.58" />
            <path fill="#B5B6B6" d="M125.38,3.48c47.76,0,90.41,30.28,106.14,75.36l-13.49,4.7c-13.72-39.35-50.96-65.78-92.65-65.78c-54.09,0-98.1,44.01-98.1,98.1H13C13,53.89,63.41,3.48,125.38,3.48" />
            <polygon fill="#B5B6B6" points="237,66.72 205.29,81.1 234.07,102.42" />
            <path fill="#010101" d="M77.47,139.14c0,3.08,0.49,5.64,1.49,7.67c1.84,3.73,5.18,5.59,10.04,5.59c2.97,0,5.57-1.02,7.79-3.06c2.21-2.04,3.32-4.98,3.32-8.81c0-5.08-2.05-8.47-6.16-10.18c-2.33-0.96-6.02-1.43-11.04-1.43v-10.87c4.92-0.07,8.35-0.55,10.29-1.44c3.36-1.49,5.04-4.51,5.04-9.06c0-2.95-0.86-5.34-2.57-7.19c-1.72-1.85-4.14-2.77-7.25-2.77c-3.58,0-6.21,1.14-7.89,3.41c-1.68,2.27-2.49,5.31-2.41,9.11H63.94c0.14-3.84,0.8-7.48,1.97-10.92c1.25-3.02,3.19-5.8,5.85-8.36c1.99-1.81,4.34-3.2,7.07-4.15c2.73-0.96,6.08-1.44,10.06-1.44c7.38,0,13.33,1.91,17.85,5.73c4.52,3.82,6.78,8.94,6.78,15.37c0,4.55-1.35,8.38-4.05,11.5c-1.7,1.96-3.48,3.28-5.33,4c1.38,0,3.37,1.19,5.97,3.56c3.87,3.59,5.81,8.49,5.81,14.7c0,6.53-2.27,12.27-6.78,17.23c-4.53,4.95-11.22,7.43-20.08,7.43c-10.93,0-18.52-3.57-22.77-10.71c-2.24-3.8-3.48-8.77-3.73-14.91H77.47z" />
            <path fill="#010101" d="M167.68,88.63c5.32,1.75,9.62,4.95,12.91,9.61c2.64,3.77,4.45,7.85,5.4,12.23c0.96,4.39,1.44,8.57,1.44,12.55c0,10.08-2.02,18.61-6.07,25.6c-5.49,9.42-13.96,14.14-25.41,14.14h-32.67V86.99h32.67C160.66,87.06,164.56,87.6,167.68,88.63 M138.65,100.15v49.46h14.63c7.48,0,12.7-3.68,15.65-11.06c1.61-4.04,2.42-8.86,2.42-14.44c0-7.71-1.21-13.64-3.64-17.76c-2.41-4.13-7.23-6.2-14.44-6.2H138.65z" />
        </svg>
    );

    add3dRender = (baseCode, counter) => {
        const { trackEvent } = this.props;

        trackEvent({
            eventCategory: 'Product Page',
            eventAction: 'Alt Images',
            eventLabel: '3D Click',
        });
        trackEvent({
            eventCategory: 'poduct image',
            eventAction: 'click',
            eventLabel: `${baseCode} | ${counter}`,
        });
    }

    isGif = (name, classes) => {
        if (name.includes('_gif')) {
            return <div className={classes.circlePlay}><CirclePlay /></div>;
        }
        return <></>;
    }

    render() {
        const {
            featureFlags,
            classes,
            data,
            selectedSku,
            smartGiftLetThemKnowSection,
            isSmartGiftEnabled,
            brand,
            isClubProduct,
            clubComponents,
            renderSnipe,
            renderHeroImageFlag,
            altImagesContainerStyle,
            productBaseContent,
            presentation_family,
            setShippingModalOpen,
            productUrl,
            partNumber,
        } = this.props;
        const {
            mainImage,
            altImages,
            path,
            heroImage,
            isVideo,
            videoUrl,
        } = this.state;

        const videoBlock = productBaseContent?.entries?.[0]?.product_page_video || {};
        const previewImage = videoBlock?.static_image_preview?.url || '';
        const contentType = videoBlock?.video?.content_type || '';
        const videoClass =  classes.simpleVideo;

        let isProductHasSmartGiftEnabled = true; // for flowers brand handle by LD.
        if (presentation_family === 'food') {
            isProductHasSmartGiftEnabled = data?.isSmartgiftEnabled; // food brands will use product based smart gift
        }

        // Use heroImage state to update pdp images when skus are changed
        // To use only the main image and not update by sku, use presentation logic
        // to grab image source from data.image
        const personalizationFlag = featureFlags['is-personalization-enabled'] ?? false;

        // Added logic to render the snipe image on the main image only
        const altImagePath = heroImage?.split('/');
        const mainImagePath = mainImage?.fullPath?.split('/');
        // Check if the last segment of the alt image path matches the last segment of the main image path
        const showSnipeImage = altImagePath?.[altImagePath?.length - 1] === mainImagePath?.[mainImagePath?.length - 1];

        const setLayout = () => {
            const imageSource = heroImage;
            return (
                isVideo ?  (
                    <VideoComponent
                        classes={classes}
                        videoUrl={videoUrl}
                        previewImage={previewImage}
                        videoClass={videoClass}
                        contentType={contentType}
                        videoBlock={videoBlock}
                        productBaseCode={data?.baseCode}
                    />
                )
                    : (
                        <>
                            {
                                this.renderZoom() && typeof window !== 'undefined' && (
                                    <ProductImageZoomingUI
                                        imagePath={heroImage}
                                        data={data}
                                        classes={classes}
                                        isRenderSnipe={renderSnipe && showSnipeImage}
                                        personalizationFlag={personalizationFlag}
                                    />
                                )
                            }
                            {!this.renderZoom() && (
                                <div
                                    id="responsive-wrapper"
                                >
                                    <ResponsiveImage
                                        className={classes.productImage}
                                        path={imageSource}
                                        alt={ReactHTMLParser(data?.name)}
                                        params={{
                                            desktop: 'width=585&auto=webp',
                                            tablet: 'width=406&height=445auto=webp',
                                        }}
                                        breakpoints={{
                                            desktop: '1024px',
                                            tablet: '640px',
                                        }}
                                    />
                                    {renderSnipe && showSnipeImage && shouldRenderSnipe(data?.image?.snipe, personalizationFlag) && isSnipeImageActive(data?.image, 'pdp')  ? ( // if snipe data exist display snipe img
                                        <LazyLoad>
                                            <Fade in timeout={700}>
                                                <SnipeRender
                                                    altText={data?.image?.altText}
                                                    styleProp={classes.snipe}
                                                    src={`${data?.image?.snipeImagePath}${data?.image?.snipe}_z.gif?auto=webp`}
                                                    fallbackSrc={`${snipePath(data.image.path)}${data?.image?.snipe}_z.gif?auto=webp`}
                                                />
                                            </Fade>
                                        </LazyLoad>
                                    ) : null}
                                    {showSnipeImage && <SnipeRenderQuery  partNumber={partNumber || data?.partNumber || data?.id}  productType="product" />}
                                    <FavoritesUI partNumber={partNumber || data?.partNumber || data?.id} />
                                </div>
                            )}
                        </>
                    )
            );
        };

        const renderAlternateImages = () => {
            const { trackEvent } = this.props;
            const trackEventObj = {
                eventCategory: 'product image',
                eventAction: 'click',
            };
            const ariaLabelledByID = (name, index) => `${ReactHTMLParser(name).toString().replace(/\s|\W/g, '_')}_alternate_image_${index || ''}`;// replace whitespace or non-word char with underscore

            return (
                <div className={`${classes.altImageContainer} ${altImagesContainerStyle} altImagesContainer`}>
                    <button
                        type="button"
                        className={classes.altImageButton}
                        aria-labelledby={ariaLabelledByID(ReactHTMLParser(mainImage?.imgName))}
                        onClick={() => {
                            this.trackProductImageClick();
                            this.setProductImage(selectedSku?.image?.name);
                            trackEvent({
                                ...trackEventObj,
                                eventLabel: `${data?.baseCode} | ${this.buildImgPath(selectedSku?.image?.path, selectedSku?.image?.name)}| 1`,
                            });
                        }}
                    >
                        <ResponsiveImage path={this.buildImgPath(selectedSku?.image?.path, selectedSku?.image?.name)} alt={ReactHTMLParser(mainImage?.imgName)} className={classes.altImage} params={{ desktop: 'width=140&height=153', tablet: 'width=140&height=153', mobile: 'width=140&height=153' }} dimensions={{ width: '140px', height: '153px' }} />
                        <span hidden id={ariaLabelledByID(ReactHTMLParser(mainImage?.imgName))}>{`${ReactHTMLParser(mainImage?.imgName)} main image`}</span>
                    </button>
                    {videoBlock?.enabled && (
                        <button
                            type="button"
                            className={`${classes.altImageButton} ${classes.videoAltButton}`}
                            aria-labelledby={ariaLabelledByID(`${ReactHTMLParser(mainImage?.imgName)}_video`)}
                            onClick={() => {
                                this.trackProductImageClick();
                                this.setProductImage(videoBlock?.video?.url, videoBlock?.video?.content_type);
                                trackEvent({
                                    ...trackEventObj,
                                    eventLabel: `${data?.baseCode} | ${previewImage}| 2`,
                                });
                            }}
                        >
                            <ResponsiveImage path={previewImage} alt="Video" className={classes.altImage} params={{ desktop: 'quality=100', tablet: 'quality=100', mobile: 'quality=100' }} />
                            <span hidden id={`${ReactHTMLParser(mainImage?.imgName)}_video`}>{`${ReactHTMLParser(mainImage?.imgName)} video`}</span>
                        </button>
                    )}
                    {altImages.map((imgName, index) => (
                        <button
                            key={`btnImg${imgName}`}
                            type="button"
                            className={classes.altImageButton}
                            aria-labelledby={ariaLabelledByID(ReactHTMLParser(mainImage?.imgName), index)}
                            onClick={() => {
                                this.trackProductImageClick();
                                this.setProductImage(imgName);
                                trackEvent({
                                    ...trackEventObj,
                                    eventLabel: `${data?.baseCode} | ${this.buildImgPath(path, imgName)}| ${videoBlock?.enabled ? index + 3 : index + 2}`,
                                });
                            }}
                        >
                            {this.isGif(imgName, classes)}
                            <img
                                src={`${this.buildImgPath(path, imgName, true)}${imgName.includes('_gif') ? '&' : '?'}width=140`}
                                alt={mainImage?.imgName}
                                className={classes.altImage}
                            />
                            <span hidden id={ariaLabelledByID(ReactHTMLParser(mainImage?.imgName), index)}>{`${ReactHTMLParser(mainImage?.imgName)} alternate image ${index}`}</span>
                        </button>
                    ))}
                </div>
            );
        };

        if (!renderHeroImageFlag && isClubProduct) {
            const { modalOpen, selectedComponentMonth } = this.state;
            return (
                <div
                    data-testid="rendered-club-image"
                >
                    <GraphqlClubPDPImage
                        modalOpen={modalOpen}
                        handleState={this.handleState}
                        selectedComponentMonth={selectedComponentMonth}
                        cdnLink={this.cdnLink}
                        clubComponents={clubComponents}
                        product={data}
                        navigate={this.navigate}
                        getSlides={this.getSlides}
                        renderZoom={this.renderZoom}
                        shouldRenderSnipe={shouldRenderSnipe}
                        getClubLengthDescription={this.getClubLengthDescription}
                        selectedSku={selectedSku}
                        heroImage={heroImage}
                        personalizationFlag={personalizationFlag}
                        setShippingModalOpen={setShippingModalOpen}
                        productUrl={productUrl}
                        showSnipeImage={showSnipeImage}
                    />
                </div>
            );
        }
        const breakpoint = featureFlags['is-pdp-alt-v3-enabled'] ? '(max-width: 1025px)' : '(max-width: 1279px)';
        return (
            <>
                <div className={classes.productImageWrapperInner}>
                    {setLayout()}
                </div>
                <Media
                    query={breakpoint}
                >
                    {(matches) => (matches
                        && (
                            featureFlags['pdp-image-sticky']
                                ? (
                                    <CarouselAltImg
                                        classes={classes}
                                        buildImgPath={this.buildImgPath}
                                        setProductImage={this.setProductImage}
                                        selectedSku={selectedSku}
                                        videoBlock={videoBlock}
                                        data={data}
                                        add3dRender={this.add3dRender}
                                        previewImage={previewImage}
                                        get3dSvg={this.get3dSvg}
                                        mainImage={mainImage}
                                        altImages={altImages}
                                        path={path}
                                    />
                                ) : renderAlternateImages()
                        ))}
                </Media>
                {isSmartGiftEnabled && !isProductHasSmartGiftEnabled
                            && (
                                <SmartGiftContainer>
                                    <SmartGift
                                        giftData={smartGiftLetThemKnowSection}
                                        partNumber={data.partNumber}
                                        productName={data.name}
                                        brand={brand}
                                        sku={selectedSku?.id}
                                        price={selectedSku?.prices}
                                        image={heroImage}
                                        featureFlags={featureFlags}
                                    />
                                </SmartGiftContainer>
                            )}
                <Media query={breakpoint}>
                    {(matches) => (!matches
                            && (altImages && !renderHeroImageFlag) && renderAlternateImages()
                    )}
                </Media>
            </>
        );
    }
}

ProductImageUIGrid.propTypes = {
    classes: object.isRequired,
    data: object.isRequired,
    featureFlags: object,
    location: object.isRequired,
    presentation_family: string.isRequired,
    selectedSku: object.isRequired,
    smartGiftLetThemKnowSection: shape({
        button_copy: string,
        button_color: string,
        copy: string,
    }),
    brand: shape({
        domain: string,
    }),
    isSmartGiftEnabled: bool,
    isClubProduct: bool,
    clubComponents: clubComponentType,
    renderSnipe: bool,
    renderHeroImageFlag: bool,
    altImagesContainerStyle: string,
    productBaseContent: shape({
        entries: arrayOf(shape({
            products_viewed_section: shape({
                background_color: string,
                copy: string,
                font_color: string,
            }),
        })),
    }),
    trackEvent: func.isRequired,
    emitCustomTrackingEvent: func.isRequired,
    pmallSummaryInfo: shape({
        customizations: shape({
            refnum: string,
            imageURL: string,
        }),
    }).isRequired,
    setShippingModalOpen: func,
    productUrl: string,
    partNumber: string,
    selectedSkuBasedAltImages: bool,
};

ProductImageUIGrid.defaultProps = {
    isClubProduct: false,
    clubComponents: [],
    brand: {},
    smartGiftLetThemKnowSection: {},
    isSmartGiftEnabled: false,
    renderSnipe: true,
    renderHeroImageFlag: false,
    altImagesContainerStyle: '',
    productBaseContent: {},
    setShippingModalOpen: () => { },
    featureFlags: {},
    productUrl: '',
    partNumber: '',
    selectedSkuBasedAltImages: false,
};

const mapDispatchToProps = (dispatch) => ({
    trackEvent: bindActionCreators(tagManagerActions.trackEvent, dispatch),
    emitCustomTrackingEvent: bindActionCreators(emitCustomTrackingEvent, dispatch),
});

const mapStateToProps = (state) => ({
    pmallSummaryInfo: getPmallSummaryInfo(state),
});

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(ProductImageUIGrid));
